import { createContext, useContext, ReactNode, useState, useEffect } from 'react';

interface SiteTheme {
  colors: Record<string, string>;
  fonts: Record<string, string>;
  layout: Record<string, number>;
  config: Record<string, any>;
  updateTheme: (updates: Partial<SiteTheme>) => void;
}

const defaultTheme: SiteTheme = {
  colors: {
    primary: '#1E40AF',
    primary_hover: '#1E3A8A',
    secondary: '#4F46E5',
    accent: '#3B82F6',
    background: '#030712',
    foreground: '#F1F5F9',
    card: '#111827',
    card_hover: '#1F2937',
    muted: '#6B7280',
    muted_foreground: '#9CA3AF',
    border: '#374151',
    input: '#374151',
    ring: '#1E40AF',
    gradient_primary: '#1E40AF,#3B82F6',
    gradient_secondary: '#4F46E5,#6366F1',
    gradient_accent: '#3B82F6,#60A5FA',
    gradient_hero: '#030712,#0C1222,#111827'
  },
  fonts: {
    font_family_display: 'Inter, system-ui, sans-serif',
    font_family_body: 'Inter, system-ui, sans-serif',
    font_size_base: '16',
    font_size_display_lg: '72',
    font_size_display_md: '56',
    font_size_display_sm: '40',
    line_height_tight: '1.25',
    line_height_normal: '1.5',
    line_height_relaxed: '1.75'
  },
  layout: {
    container_width: 1280,
    border_radius: 16,
    spacing_unit: 4,
    max_width_content: 768
  },
  config: {
    site_name: 'Cássio Lemos - Portfólio 2026',
    site_description: '',
    contact_email: '',
    contact_phone: '',
    social_links: {},
    seo_title: '',
    seo_description: ''
  },
  updateTheme: () => { }
};

const ThemeContext = createContext<SiteTheme>(defaultTheme);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<SiteTheme>(defaultTheme);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('site-theme');
      if (savedTheme) {
        try {
          const parsedTheme = JSON.parse(savedTheme);
          setTheme(parsedTheme);
        } catch (error) {
          console.error('Erro ao carregar tema:', error);
        }
      }
    }
  }, []);

  const updateTheme = (updates: Partial<SiteTheme>) => {
    setTheme(prev => {
      const newTheme = { ...prev, ...updates };
      localStorage.setItem('site-theme', JSON.stringify(newTheme));

      // Aplicar as mudanças ao DOM
      if (typeof document !== 'undefined') {
        const root = document.documentElement;

        if (updates.colors) {
          Object.entries(updates.colors).forEach(([key, value]) => {
            root.style.setProperty(`--${key}`, value);
          });
        }

        if (updates.fonts) {
          Object.entries(updates.fonts).forEach(([key, value]) => {
            root.style.setProperty(`--${key}`, value);
          });
        }
      }

      return newTheme;
    });
  };

  return (
    <ThemeContext.Provider value={{ ...theme, updateTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  return context;
}
